import { FormattedMessage } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import {
  ArrowRight,
  CookingPot,
  Eye,
  EyeOff,
  GraduationCap,
  Lock,
  Mail,
  Tractor,
  User,
} from 'lucide';

//common
import Icon from 'common/components/Icon';
import useGeneralHook from 'common/hook/useGeneralHook';
import SchemaForm from 'common/SchemaForm';
import ConfirmationCodeDialog from 'modules/auth/components/ConfirmationCodeDialog';
import { logIn, logInAsGuest, LoginReqData } from '@redux/authReducer';
import { AppState } from '@redux/store';
import { LogoFull } from 'svg';
import { brandLogoUrl, getBrand } from 'config/brand';
import { PASSWORD } from 'config/regex';
import { TERM_OF_SERVICE_URL } from 'api/API_App';

// Nền trắng; màu chủ đạo + màu nhấn lấy từ theme để đổi theo PRIMARY_COLOR của từng deployment.
const MUTED = '#7C8B99';
const LINE = '#D7E2EE';
const FIELD = '#F4F8FB';
const HERO = '#164863';
const GLOW = '#7EB6D0';

const useLoginPalette = () => {
  const { palette } = useTheme();
  const primary = palette.primary.main;

  return {
    primary,
    farm: palette.secondary.main,
    kitchen: primary,
    school: lighten(primary, 0.35),
  };
};

type NetNode = { x: number; y: number };

// Toạ độ theo % của vùng mạng lưới; cạnh nối vẽ bằng SVG viewBox 0–100 cùng hệ toạ độ.
const FARMS: NetNode[] = [
  { x: 8, y: 18 },
  { x: 20, y: 46 },
  { x: 6, y: 76 },
  { x: 26, y: 92 },
  { x: 32, y: 66 },
];
const KITCHENS: NetNode[] = [
  { x: 50, y: 30 },
  { x: 55, y: 72 },
  { x: 44, y: 94 },
];
const SCHOOLS: NetNode[] = [
  { x: 78, y: 4 },
  { x: 93, y: 20 },
  { x: 88, y: 60 },
  { x: 95, y: 84 },
  { x: 78, y: 90 },
];

const FARM_EDGES: [number, number][] = [
  [0, 0], [0, 1], [1, 0], [1, 1], [2, 1], [2, 2], [3, 2], [3, 1], [4, 0], [4, 2],
];
const SCHOOL_EDGES: [number, number][] = [
  [0, 0], [0, 1], [0, 2], [1, 2], [1, 3], [1, 4], [2, 4], [2, 3], [2, 1],
];
// Hành trình được làm nổi: trang trại 1 → bếp 0 → trường 2.
const HIGHLIGHT: [NetNode, NetNode][] = [
  [FARMS[1], KITCHENS[0]],
  [KITCHENS[0], SCHOOLS[2]],
];

const curve = (a: NetNode, b: NetNode) =>
  `M${a.x} ${a.y} Q${(a.x + b.x) / 2} ${Math.min(a.y, b.y) - 10} ${b.x} ${b.y}`;

const NETWORK_CHIPS = [
  { node: FARMS[1], side: 'right', title: 'Trang trại', detail: 'Nguồn gốc nguyên liệu', icon: Tractor },
  { node: KITCHENS[0], side: 'right', title: 'Bếp ăn', detail: 'Chế biến và kiểm soát', icon: CookingPot },
  { node: SCHOOLS[2], side: 'left', title: 'Bàn ăn', detail: 'Suất ăn đến học sinh', icon: GraduationCap },
] as const;

const NetworkNode = ({ node, color, size }: { node: NetNode; color: string; size: number }) => (
  <Box
    sx={{
      position: 'absolute',
      left: `${node.x}%`,
      top: `${node.y}%`,
      width: size,
      height: size,
      transform: 'translate(-50%, -50%)',
      borderRadius: '50%',
      bgcolor: color,
      boxShadow: `0 0 0 5px ${alpha(color, 0.14)}, 0 0 0 11px ${alpha(color, 0.06)}`,
    }}
  />
);

const DataNetwork = () => {
  const colors = useLoginPalette();

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left: { laptop: 48, desktop: 80 },
        right: { laptop: 32, desktop: 56 },
        top: 'max(40%, 380px)',
        bottom: 88,
        pointerEvents: 'none',
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
          '@keyframes login-pulse': {
            from: { strokeDashoffset: 100 },
            to: { strokeDashoffset: 0 },
          },
          '& .pulse': {
            strokeDasharray: '10 90',
            strokeDashoffset: 100,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'login-pulse 3.2s linear infinite',
            },
          },
        }}
      >
        {FARM_EDGES.map(([f, k]) => (
          <path
            key={`f${f}k${k}`}
            d={curve(FARMS[f], KITCHENS[k])}
            fill="none"
            stroke={GLOW}
            strokeOpacity={0.35}
            strokeWidth={1.2}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {SCHOOL_EDGES.map(([k, s]) => (
          <path
            key={`k${k}s${s}`}
            d={curve(KITCHENS[k], SCHOOLS[s])}
            fill="none"
            stroke={colors.primary}
            strokeOpacity={0.55}
            strokeWidth={1.2}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {HIGHLIGHT.map(([a, b], index) => (
          <g key={index}>
            <path
              d={curve(a, b)}
              fill="none"
              stroke={GLOW}
              strokeWidth={3}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ filter: `drop-shadow(0 0 6px ${alpha(GLOW, 0.35)})` }}
            />
            <path
              className="pulse"
              d={curve(a, b)}
              pathLength={100}
              fill="none"
              stroke="#fff"
              strokeWidth={3}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ animationDelay: `${index * 1.6}s` }}
            />
          </g>
        ))}
      </Box>

      {FARMS.map((node, index) => (
        <NetworkNode key={`f${index}`} node={node} color={colors.farm} size={12} />
      ))}
      {KITCHENS.map((node, index) => (
        <NetworkNode key={`k${index}`} node={node} color={colors.kitchen} size={16} />
      ))}
      {SCHOOLS.map((node, index) => (
        <NetworkNode key={`s${index}`} node={node} color={colors.school} size={12} />
      ))}

      {NETWORK_CHIPS.map(({ node, side, title, detail, icon }) => (
        <Box
          key={title}
          sx={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: side === 'right' ? 'translate(16px, -50%)' : 'translate(calc(-100% - 16px), -50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pl: 0.75,
            pr: 1.75,
            py: 0.75,
            borderRadius: '14px',
            bgcolor: 'rgba(8, 32, 48, 0.45)',
            border: '1px solid rgba(232, 244, 250, 0.28)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 10px 28px rgba(8, 28, 44, 0.22)',
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              bgcolor: 'rgba(255,255,255,0.14)',
              color: '#F4F8FB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon icon={icon} size={18} />
          </Box>
          <Box>
            <Typography sx={{ color: '#F4F8FB', fontSize: 13.5, fontWeight: 700, lineHeight: 1.5 }}>
              {title}
            </Typography>
            <Typography sx={{ color: 'rgba(244, 248, 251, 0.72)', fontSize: 11.5, lineHeight: 1.5 }}>
              {detail}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const LoginHero = () => {
  const colors = useLoginPalette();
  const legend = [
    { label: 'Trang trại', color: colors.farm },
    { label: 'Bếp ăn', color: colors.kitchen },
    { label: 'Trường học', color: colors.school },
  ];

  return (
    <Box
      sx={{
        display: { xs: 'none', laptop: 'flex' },
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        position: 'relative',
        flexDirection: 'column',
        overflow: 'hidden',
        color: '#F3F8FC',
        bgcolor: HERO,
        borderRadius: '24px',
        backgroundImage: `radial-gradient(50% 45% at 30% 70%, ${alpha(GLOW, 0.16)}, transparent 72%)`,
        px: { laptop: 6, desktop: 10 },
        py: 7,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `radial-gradient(${alpha('#F3F8FC', 0.22)} 1.2px, transparent 1.2px)`,
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(55% 60% at 45% 65%, #000 20%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(55% 60% at 45% 65%, #000 20%, transparent 85%)',
        }}
      />
      <DataNetwork />

      <Box sx={{ position: 'relative', alignSelf: 'flex-start', bgcolor: '#fff', borderRadius: '12px', px: 1.75, py: 1 }}>
        {getBrand().has_logo ? (
          <img src={brandLogoUrl()} alt={getBrand().app_name} style={{ height: 44, objectFit: 'contain' }} />
        ) : (
          <LogoFull style={{ height: 44, width: 'auto' }} />
        )}
      </Box>

      <Box sx={{ position: 'relative', mt: 'clamp(32px, 8vh, 88px)', maxWidth: 900 }}>
        <Typography sx={{ color: GLOW, fontSize: 13, fontWeight: 700, letterSpacing: '0.2em' }}>
          NỀN TẢNG TRUY XUẤT SUẤT ĂN
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 2,
            color: '#F3F8FC',
            fontWeight: 800,
            fontSize: 'clamp(34px, 3.2vw, 64px)',
            lineHeight: 1.25,
            letterSpacing: '-0.03em',
            // Ngắt dòng cố định: "Cổng thông tin minh bạch" / "doanh nghiệp".
            '& > span': { display: 'block', whiteSpace: 'nowrap' },
          }}
        >
          <span>Cổng thông tin minh bạch</span>
          <span>
            <Box component="span" sx={{ color: GLOW }}>
              doanh nghiệp
            </Box>
          </span>
        </Typography>
        <Typography
          sx={{
            mt: 2.25,
            maxWidth: 580,
            fontSize: 'clamp(16px, 1.1vw, 19px)',
            lineHeight: 1.6,
            color: '#B7C9D6',
          }}
        >
          Kết nối trang trại, bếp ăn và nhà trường trong một mạng lưới dữ liệu minh bạch.
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', mt: 'auto', display: 'flex', gap: 3.5 }}>
        {legend.map(({ label, color }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
            <Typography sx={{ fontSize: 13, color: '#B7C9D6' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const inputSx = {
  '& .MuiInputBase-root': {
    height: 50,
    borderRadius: '12px',
    bgcolor: FIELD,
    transition: 'background-color .15s',
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiOutlinedInput-notchedOutline': { top: 0, borderColor: LINE },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiInputBase-input': { fontSize: 15 },
  // Nhãn cố định phía trên ô nhập thay vì nổi trên viền.
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    mb: 1,
    fontSize: 13.5,
    fontWeight: 600,
    color: 'text.primary',
    '&.Mui-focused': { color: 'text.primary' },
  },
};

const LoginPage = () => {
  const { loading, pendingConfirmationCode } = useSelector((s: AppState) => s.auth, shallowEqual);
  const { dispatch, enqueueSnackbar } = useGeneralHook();
  const colors = useLoginPalette();

  const handleLogin = (values: LoginReqData) => {
    const trimmedValues = {
      handle: values.handle.trim(),
      password: values.password.trim(),
    };

    if (!trimmedValues.handle || !trimmedValues.password) {
      enqueueSnackbar({
        message: 'Vui lòng điền đầy đủ thông tin đăng nhập',
        variant: 'error',
      });

      return;
    }

    dispatch(logIn(trimmedValues));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: '#F4F8FB',
        backgroundImage: `radial-gradient(42% 50% at 78% 18%, ${alpha(colors.primary, 0.08)}, transparent 70%)`,
        p: { xs: 0, laptop: 2.5 },
        gap: { xs: 0, laptop: 2.5 },
      }}
    >
      <ConfirmationCodeDialog open={pendingConfirmationCode} />
      <LoginHero />
      <Box
        component="main"
        sx={{
          position: 'relative',
          width: { xs: '100%', laptop: 'clamp(480px, 31vw, 600px)' },
          flexShrink: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: { xs: 0, laptop: '24px' },
          boxShadow: { laptop: `0 0 0 1px ${LINE}, 0 24px 60px -24px rgba(7, 20, 34, 0.28)` },
          px: { xs: 3, tablet: 6, desktop: 10 },
          py: { xs: 4, tablet: 6 },
          '@media (max-height: 820px)': { py: 4 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 5,
            '@media (max-height: 820px)': { py: 2 },
          }}
        >
          <SchemaForm
            onSubmit={handleLogin}
            formHookProps={{
              defaultValues: {
                handle: '',
                password: '',
              },
            }}
            formProps={{ sx: { width: '100%', maxWidth: 400 } }}
            schema={{
              fields: ({ formProps: { intl, state, setState } }) => {
                return {
                  title: {
                    mode: 'node',
                    render: () => (
                      <Box sx={{ mb: 1, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: 'primary.main',
                            fontWeight: 900,
                            fontSize: { xs: 40, tablet: 48 },
                            letterSpacing: '0.08em',
                            lineHeight: 1,
                          }}
                        >
                          GANET
                        </Typography>
                        <Typography
                          component="h2"
                          sx={{
                            mt: 3,
                            '@media (max-height: 820px)': { mt: 2 },
                            fontSize: 26,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: 'text.primary',
                          }}
                        >
                          <FormattedMessage id="login" />
                        </Typography>
                        <Typography
                          sx={{
                            mt: 1,
                            fontSize: 15,
                            lineHeight: 1.55,
                            color: 'text.secondary',
                            '& > span': { display: 'block' },
                            '@media (max-height: 820px)': { display: 'none' },
                          }}
                        >
                          <span>Truy cập Cổng thông tin minh bạch</span>
                          <span>doanh nghiệp.</span>
                        </Typography>
                      </Box>
                    ),
                  },
                  handle: {
                    mode: 'text-field',
                    label: intl.formatMessage({ id: 'email' }),
                    placeholder: 'Nhập email',
                    showRequiredAsterisk: false,
                    rules: { required: true },
                    sx: inputSx,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 1.5, color: MUTED }}>
                          <Icon icon={Mail} size={20} />
                        </InputAdornment>
                      ),
                    },
                  },
                  password: {
                    mode: 'text-field',
                    type: state.showPassword ? 'text' : 'password',
                    label: intl.formatMessage({ id: 'password' }),
                    placeholder: 'Nhập mật khẩu',
                    showRequiredAsterisk: false,
                    sx: inputSx,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 1.5, color: MUTED }}>
                          <Icon icon={Lock} size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 0.5 }}>
                          <IconButton
                            onClick={() => {
                              setState({ showPassword: !state.showPassword });
                            }}
                            aria-label={state.showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                            sx={{ color: MUTED }}
                          >
                            <Icon icon={state.showPassword ? EyeOff : Eye} size={16} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                    rules: {
                      required: true,
                      minLength: {
                        value: 8,
                        message: intl.formatMessage({ id: 'account.error.minLength' }),
                      },
                      pattern: {
                        value: PASSWORD,
                        message: intl.formatMessage({ id: 'account.error.invalidPassword' }),
                      },
                    },
                  },
                  submit: {
                    mode: 'button',
                    type: 'submit',
                    children: intl.formatMessage({ id: 'login' }),
                    variant: 'contained',
                    fullWidth: true,
                    loading: loading,
                    endIcon: <Icon icon={ArrowRight} size={16} />,
                    sx: {
                      height: 50,
                      borderRadius: '12px',
                      fontSize: 15,
                      boxShadow: `0 12px 24px -10px ${alpha(colors.primary, 0.45)}`,
                    },
                  },
                  divider: {
                    mode: 'node',
                    render: () => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&::before, &::after': { content: '""', flex: 1, borderTop: `1px solid ${LINE}` },
                        }}
                      >
                        <Typography sx={{ fontSize: 13, color: MUTED }}>hoặc</Typography>
                      </Box>
                    ),
                  },
                  continueAsGuest: {
                    mode: 'node',
                    render: () => (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        disabled={loading}
                        startIcon={<Icon icon={User} size={16} />}
                        sx={{ height: 50, borderRadius: '12px', fontSize: 15 }}
                        onClick={() => dispatch(logInAsGuest())}
                      >
                        <FormattedMessage id="continueAsGuest" />
                      </Button>
                    ),
                  },
                };
              },
            }}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.6 }}>
            <FormattedMessage id="termOfService" />{' '}
            <Link
              href={TERM_OF_SERVICE_URL}
              underline="hover"
              sx={{ color: 'secondary.main', fontWeight: 600 }}
              target="_blank"
            >
              Điều khoản sử dụng
            </Link>
            .
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 12.5, color: MUTED }}>
            © {getBrand().copyright}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
export default LoginPage;
